import React, { FC, useEffect, useMemo, useState } from "react";
import {
  EmissionFactorDto,
  EmissionFactorSource,
  EmissionFactorType,
} from "@green0meter/api-client";
import { IconButton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";

import EmissionFactorEditor from "../EmissionFactorEditor";
import ImportEmissionFactorsButton from "./components/ImportEmissionFactorsButton";
import ExportEmissionFactorsButton from "./components/ExportEmissionFactorsButton";

import {
  useDuplicateEmissionFactor,
  useGetEmissionFactors,
  useRemoveEmissionFactor,
} from "@api";
import { ConfirmationModal, Table } from "@components/common";
import { useUserStore } from "@store/user";
import { Box, Button, Chip, Stack } from "@components/base";
import { TableColumn } from "@components/common/Table";
import { DEFAULT_PAGE_SIZE } from "@constants";

interface EmissionFactorsTable {
  customEmissionFactors?: boolean;
}

const EmissionFactorsTable: FC<EmissionFactorsTable> = ({
  customEmissionFactors = false,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { emissionFactors, isPendingGetEmissionFactors } =
    useGetEmissionFactors(user);
  const [activeEmissionFactor, setActiveEmissionFactor] =
    useState<EmissionFactorDto>();
  const [removeEmissionFactorDialog, setRemoveEmissionFactorDialog] =
    useState<string>();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  const { removeEmissionFactor } = useRemoveEmissionFactor(user);
  const { duplicateEmissionFactor } = useDuplicateEmissionFactor(user);

  const emissionFactorsList = useMemo(
    () =>
      emissionFactors
        ?.filter((factor) =>
          customEmissionFactors ? factor.companyId : !factor.companyId,
        )
        .slice(currentPage * pageSize, currentPage * pageSize + pageSize),
    [emissionFactors, customEmissionFactors, currentPage, pageSize],
  );

  useEffect(() => {
    if (emissionFactors) {
      setTotalItems(
        emissionFactors.filter((factor) =>
          customEmissionFactors ? factor.companyId : !factor.companyId,
        ).length,
      );
    }
  }, [emissionFactors, customEmissionFactors]);

  const handleEditEmissionFactor = (emissionFactor: EmissionFactorDto): void => {
    setActiveEmissionFactor(emissionFactor);
  };

  const handleRemoveEmissionFactor = (): void => {
    if (removeEmissionFactorDialog) {
      removeEmissionFactor(removeEmissionFactorDialog);
      setRemoveEmissionFactorDialog(undefined);
    }
  };

  const handleDuplicateEmissionFactor = (emissionFactorId: string): void => {
    duplicateEmissionFactor(emissionFactorId);
  };

  const handleCreateEmissionFactor = (): void => {
    const newEmissionFactor: EmissionFactorDto = {
      id: "",
      factor: 0,
      unit: { id: "", name: "", abbreviation: "" },
      pairs: [],
      year: new Date().getFullYear(),
      type: EmissionFactorType.Basic,
      source: EmissionFactorSource.Defra,
      sourceDetail: "",
    };

    setActiveEmissionFactor(newEmissionFactor);
  };

  const columns: TableColumn<EmissionFactorDto>[] = [
    {
      key: "pairs",
      dataKey: "pairs",
      title: t("emissionFactorEditor.questionCodesLabel"),
      renderCellContent: (emissionFactor) => (
        <Box display="flex" flexWrap="wrap" gap={1}>
          {emissionFactor.pairs.map((pair) => (
            <Chip
              key={pair.id}
              label={pair.questionCode}
              color="blue1"
              size="medium"
            />
          ))}
        </Box>
      ),
    },
    {
      key: "scope",
      title: t("emissionFactorEditor.scopeLabel"),
      dataKey: "scope",
      renderCellContent: (emissionFactor) =>
        emissionFactor.scope !== undefined ? (
          <Chip
            label={emissionFactor.scope}
            color={
              emissionFactor.scope === 1
                ? "blue1"
                : emissionFactor.scope === 2
                  ? "purple1"
                  : "green"
            }
          />
        ) : null,
      tableCellProps: { width: 80 },
    },
    {
      key: "factor",
      title: t("emissionFactorEditor.factorLabel"),
      dataKey: "factor",
      tableCellProps: { width: 120 },
    },
    {
      key: "unit",
      title: t("emissionFactorEditor.unitLabel"),
      dataKey: "unit",
      renderCellContent: (emissionFactor) => (
        <Chip label={emissionFactor.unit.name} color="orange1" size="medium" />
      ),
      tableCellProps: { width: 120 },
    },
    {
      key: "actions",
      title: t("common.actions"),
      renderCellContent: (emissionFactor) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleEditEmissionFactor(emissionFactor)}
          >
            <FeatherIcon icon="edit-3" width="16" height="16" />
          </IconButton>
          <IconButton
            color="default"
            size="small"
            onClick={() => handleDuplicateEmissionFactor(emissionFactor.id)}
          >
            <FeatherIcon icon="copy" width="16" height="16" />
          </IconButton>
          <IconButton
            color="error"
            size="small"
            onClick={() => setRemoveEmissionFactorDialog(emissionFactor.id)}
          >
            <FeatherIcon icon="trash-2" width="16" height="16" />
          </IconButton>
        </Stack>
      ),
      tableCellProps: { align: "center", width: 120 },
    },
  ];

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1">
          {t("admin.emissionFactorsManagement.title")}
        </Typography>
        <Stack direction="row" spacing={2}>
          <ImportEmissionFactorsButton />
          <ExportEmissionFactorsButton />
          <Button
            variant="contained"
            size="small"
            onClick={handleCreateEmissionFactor}
            startIcon={<FeatherIcon icon="plus" width="16" height="16" />}
          >
            {t("admin.emissionFactorsManagement.addButton")}
          </Button>
        </Stack>
      </Stack>
      <Table<EmissionFactorDto>
        columns={columns}
        data={emissionFactorsList ?? []}
        isLoading={isPendingGetEmissionFactors}
        dataPlaceholder={t("admin.emissionFactorsManagement.noEmissionFactors")}
        pagination={{
          pageSize: pageSize,
          pageNumber: currentPage,
          totalItems: totalItems,
          onPageSizeChange: (size) => setPageSize(size),
          onPageNumberChange: (page) => setCurrentPage(page),
        }}
      />
      <EmissionFactorEditor
        isOpen={!!activeEmissionFactor}
        emissionFactor={activeEmissionFactor}
        onClose={() => setActiveEmissionFactor(undefined)}
        customEmissionFactor={customEmissionFactors}
      />
      <ConfirmationModal
        open={!!removeEmissionFactorDialog}
        onClose={() => setRemoveEmissionFactorDialog(undefined)}
        onConfirm={handleRemoveEmissionFactor}
        title={t("admin.emissionFactorsManagement.removeModal.title")}
        description={t("admin.emissionFactorsManagement.removeModal.description")}
      />
    </Stack>
  );
};

export default EmissionFactorsTable;
